import { IBAN_STATUS } from '@accounts/constants'
import { CurrencyIcon } from '@components'
import { TOOLTIP } from '@constants'
import { EditAccountIcon } from '@icons'
import DownloadIcon from '@icons/batch-download.svg?react'
import { CurrencyTypes, paths, useCurrencies } from 'mmfintech-backend-api'
import type { AccountBalanceResponse } from 'mmfintech-backend-api/types'
import { formatMoney, isValidArray, tr, useWindowSize } from 'mmfintech-commons'
import { Button, DataTable } from 'mmfintech-portal-commons'
import type { MouseEvent } from 'react'
import { useHistory } from 'react-router-dom'
import ReactTooltip from 'react-tooltip'

import '@accounts/views/Accounts/Accounts.scss'

interface Props {
  account: AccountBalanceResponse
  handleEdit: (account: AccountBalanceResponse) => void
  onDownloadProofOfAccount?: (accountId: number) => void
}

function AccountsRow({ account, handleEdit, onDownloadProofOfAccount }: Props) {
  const {
    id: accountId,
    currencyCode,
    balance,
    name: accountName,
    dedicatedMerchantBankAccounts,
    ibanStatus,
    currencyType
  } = account
  const history = useHistory()

  const [screenWidth] = useWindowSize()
  const { getCurrencyPrecision } = useCurrencies()

  function editAccount(e: MouseEvent<HTMLElement>) {
    e.stopPropagation()
    handleEdit(account)
  }

  const downloadProofOfAccount = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    onDownloadProofOfAccount?.(accountId)
  }

  const showIbanStatus =
    (ibanStatus === IBAN_STATUS.CREATED ||
      ibanStatus === IBAN_STATUS.PENDING ||
      ibanStatus === IBAN_STATUS.WAITING_ACTION) &&
    !dedicatedMerchantBankAccounts?.length

  function showTransactions(accountId: number): void {
    history.push(paths.banking.transactions.listByAccountId(accountId))
  }

  return (
    <DataTable.Row
      data-for='info-tooltip'
      data-tip={tr(
        'PORTAL.ACCOUNTS.TOOLTIP',
        'You can choose the account, whose balance and transactions to view here.'
      )}
      onClick={() => {
        if (screenWidth >= 768) {
          showTransactions(accountId)
        }
      }}
      data-test='table-row'
    >
      <DataTable.Cell caption={tr('PORTAL.ACCOUNTS.COLUMNS.CURRENCY', 'Currency')} className='small'>
        <div className='currency-wrapper'>
          <CurrencyIcon currency={currencyCode} size='3rem' className='mr-1 currency-icon' />
          <span data-test='currency'>{currencyCode}</span>
        </div>
      </DataTable.Cell>

      <DataTable.Cell caption={tr('PORTAL.ACCOUNTS.COLUMNS.NAME', 'Account name')}>
        <span data-test='account-name'>{accountName}</span>
      </DataTable.Cell>

      <DataTable.Cell caption={tr('PORTAL.ACCOUNTS.COLUMNS.IBAN_ISSUING', 'Iban Issuing')}>
        <span data-test='account-details'>
          {showIbanStatus
            ? tr('FRONTEND.ACCOUNTS.IBAN_REQUESTED.TITLE', 'IBAN Requested')
            : isValidArray(dedicatedMerchantBankAccounts)
              ? `IBAN: ${dedicatedMerchantBankAccounts[0]?.iban}`
              : currencyType === CurrencyTypes.CRYPTO
                ? tr('MERCHANTS.ACTIVITY.ISSUE_IBAN.NOT_ELIGIBLE', 'Not eligible')
                : tr('MERCHANTS.ACTIVITY.ISSUE_IBAN.NOT_ISSUED', 'Not issued')}
        </span>
      </DataTable.Cell>

      <DataTable.Cell caption={tr('PORTAL.ACCOUNTS.COLUMNS.ACCOUNT_ID', 'ID')} className='small'>
        <span data-test='account-id'>{accountId}</span>
      </DataTable.Cell>

      <DataTable.Cell caption={tr('PORTAL.ACCOUNTS.COLUMNS.BALANCE', 'Balance')} className='small'>
        <span data-test='balance'>{formatMoney(balance, currencyCode, getCurrencyPrecision(currencyCode))}</span>
      </DataTable.Cell>

      <DataTable.Cell className='edit-cell icon'>
        <span data-test='edit-button' onClick={editAccount} title={tr('PORTAL.ACCOUNTS.EDIT_BUTTON', 'Edit account')}>
          <EditAccountIcon />
        </span>
        {Boolean(dedicatedMerchantBankAccounts?.[0]?.iban) && (
          <span
            data-test='download-button'
            onClick={downloadProofOfAccount}
            className='ml-1'
            title={tr('PORTAL.ACCOUNTS.DOWNLOAD_PROOF', 'Download proof of account ownership')}
          >
            <DownloadIcon />
          </span>
        )}
      </DataTable.Cell>

      <DataTable.Cell className='transactions-cell no-border'>
        <Button
          color='round-primary'
          text={tr('PORTAL.ACCOUNTS.VIEW_BUTTON', 'View transactions')}
          onClick={() => showTransactions(accountId)}
          data-test='transactions-button'
        />
      </DataTable.Cell>

      <DataTable.Cell className='transactions-cell no-border'>
        <Button
          color='round-primary'
          text={tr('PORTAL.ACCOUNTS.EDIT_BUTTON', 'Edit account')}
          onClick={editAccount}
          data-test='edit-button'
        />
      </DataTable.Cell>

      <ReactTooltip
        id='info-tooltip'
        type={TOOLTIP.VARIANT.DARK}
        effect={TOOLTIP.EFFECT.SOLID}
        place={TOOLTIP.POSITION.TOP}
        multiline={true}
        isCapture={true}
      />
    </DataTable.Row>
  )
}

export default AccountsRow
